// ═══════════════════════════════════════════════════════════════
// vaultZkTotp.js — TOTP RFC 6238 pure WebCrypto (Session G)
// ═══════════════════════════════════════════════════════════════
//
// Implémentation 2FA TOTP pour le coffre-fort V2. Algo HMAC-SHA1
// 30s window 6 digits, drift ±1 step accepté. Encoding base32 RFC 4648
// pour QR code (compatible Google Authenticator / 1Password / Bitwarden).
//
// Le secret TOTP est généré côté navigateur (20 bytes random), affiché
// au user pour scan QR, puis chiffré avec la KEK passphrase et envoyé
// au backend en blob opaque. Au déverrouillage : passphrase → KEK →
// décrypt secret → user entre code → vérification HMAC locale.
//
// Limite honnête : un attaquant qui a la passphrase + un client
// modifié peut skip la vérification TOTP. Le TOTP défend surtout
// contre phishing simple où l'attaquant ne contrôle pas le client.
// ═══════════════════════════════════════════════════════════════

import { randomBytes, _internal } from './vaultZkCrypto';

const TOTP_PERIOD = 30;
const TOTP_DIGITS = 6;
const TOTP_DRIFT_STEPS = 1;

// ─────────────────────────────────────────────────────────────
// Base32 encoding (RFC 4648, sans padding)
// ─────────────────────────────────────────────────────────────

const B32_ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ234567';

export function bytesToBase32(bytes) {
  let bits = '';
  for (let i = 0; i < bytes.length; i++) {
    bits += bytes[i].toString(2).padStart(8, '0');
  }
  let out = '';
  for (let i = 0; i < bits.length; i += 5) {
    const chunk = bits.slice(i, i + 5).padEnd(5, '0');
    out += B32_ALPHABET[parseInt(chunk, 2)];
  }
  return out;
}

export function base32ToBytes(b32) {
  const cleaned = b32.toUpperCase().replace(/=+$/g, '').replace(/\s/g, '');
  let bits = '';
  for (const ch of cleaned) {
    const idx = B32_ALPHABET.indexOf(ch);
    if (idx < 0) throw new Error(`vault-zk-totp: invalid base32 char ${ch}`);
    bits += idx.toString(2).padStart(5, '0');
  }
  const bytes = [];
  for (let i = 0; i + 8 <= bits.length; i += 8) {
    bytes.push(parseInt(bits.slice(i, i + 8), 2));
  }
  return new Uint8Array(bytes);
}

// ─────────────────────────────────────────────────────────────
// HMAC-SHA1 via WebCrypto + génération OTP RFC 6238
// ─────────────────────────────────────────────────────────────

async function hmacSha1(secretBytes, msgBytes) {
  const key = await window.crypto.subtle.importKey(
    'raw',
    secretBytes,
    { name: 'HMAC', hash: 'SHA-1' },
    false,
    ['sign']
  );
  const sig = await window.crypto.subtle.sign('HMAC', key, msgBytes);
  return new Uint8Array(sig);
}

function counterToBytes(counter) {
  const buf = new Uint8Array(8);
  let v = BigInt(counter);
  for (let i = 7; i >= 0; i--) {
    buf[i] = Number(v & 0xffn);
    v >>= 8n;
  }
  return buf;
}

export async function generateTotpCode(secretBytes, atUnixSeconds = null) {
  const t = atUnixSeconds == null ? Math.floor(Date.now() / 1000) : atUnixSeconds;
  const counter = Math.floor(t / TOTP_PERIOD);
  const msg = counterToBytes(counter);
  const hash = await hmacSha1(secretBytes, msg);
  const offset = hash[hash.length - 1] & 0x0f;
  const bin =
    ((hash[offset] & 0x7f) << 24) |
    ((hash[offset + 1] & 0xff) << 16) |
    ((hash[offset + 2] & 0xff) << 8) |
    (hash[offset + 3] & 0xff);
  return String(bin % 10 ** TOTP_DIGITS).padStart(TOTP_DIGITS, '0');
}

export async function verifyTotpCode(secretBytes, code, atUnixSeconds = null) {
  if (!/^\d{6}$/.test(String(code))) return false;
  const t = atUnixSeconds == null ? Math.floor(Date.now() / 1000) : atUnixSeconds;
  for (let drift = -TOTP_DRIFT_STEPS; drift <= TOTP_DRIFT_STEPS; drift++) {
    const candidate = await generateTotpCode(secretBytes, t + drift * TOTP_PERIOD);
    // comparaison constant-time approchée : longueur identique fixée
    let diff = 0;
    for (let i = 0; i < TOTP_DIGITS; i++) {
      diff |= candidate.charCodeAt(i) ^ String(code).charCodeAt(i);
    }
    if (diff === 0) return true;
  }
  return false;
}

// ─────────────────────────────────────────────────────────────
// Setup TOTP — génère secret + URL otpauth pour QR + chiffre avec KEK
// ─────────────────────────────────────────────────────────────

export function generateTotpSecret() {
  return randomBytes(20);
}

export function buildOtpAuthUrl({ accountLabel, secretB32, issuer = 'ITRIIA Vault' }) {
  const enc = encodeURIComponent;
  const label = enc(`${issuer}:${accountLabel}`);
  return (
    `otpauth://totp/${label}` +
    `?secret=${secretB32}` +
    `&issuer=${enc(issuer)}` +
    `&algorithm=SHA1&digits=${TOTP_DIGITS}&period=${TOTP_PERIOD}`
  );
}

// ─────────────────────────────────────────────────────────────
// Wrap / unwrap secret TOTP avec la KEK passphrase
// ─────────────────────────────────────────────────────────────

export async function wrapTotpSecret(secretBytes, kek) {
  const iv = randomBytes(12);
  const ct = await window.crypto.subtle.encrypt(
    { name: 'AES-GCM', iv },
    kek,
    secretBytes
  );
  return `${_internal.bytesToBase64(iv)}.${_internal.bytesToBase64(new Uint8Array(ct))}`;
}

export async function unwrapTotpSecret(blobB64, kek) {
  try {
    const dot = blobB64.indexOf('.');
    if (dot < 0) return null;
    const iv = _internal.base64ToBytes(blobB64.slice(0, dot));
    const ct = _internal.base64ToBytes(blobB64.slice(dot + 1));
    const pt = await window.crypto.subtle.decrypt(
      { name: 'AES-GCM', iv },
      kek,
      ct
    );
    return new Uint8Array(pt);
  } catch (e) {
    return null;
  }
}

export const TOTP_CONSTANTS = Object.freeze({
  PERIOD: TOTP_PERIOD,
  DIGITS: TOTP_DIGITS,
  DRIFT_STEPS: TOTP_DRIFT_STEPS,
});
